import type { ModuleId } from './types';
import { BRANDS, type BrandId } from '../brand/brands';

/**
 * The Moderta portfolio. Source of truth for product names, positioning and one-line copy.
 * Descriptions follow docs/product-content/*.md; nothing here should claim more than those files do.
 */

export type ProductId =
  | 'mint'
  | 'mint-it'
  | 'mint-travel'
  | 'mint-business'
  | 'research-space'
  | 'native'
  | 'carbonmove'
  | 'climateviewpoint'
  | 'etrust';

/**
 * flagship       MINT, the intelligence foundation
 * specialisation a domain version of MINT
 * major          a major Moderta platform
 * independent    an independent Moderta product
 * selected       selected delivered platform work
 */
export type Tier = 'flagship' | 'specialisation' | 'major' | 'independent' | 'selected';

export type GroupId = 'mint' | 'research-space' | 'native' | 'carbonmove' | 'climateviewpoint' | 'etrust';

export type Capability = 'Intelligence' | 'Automation' | 'Integration' | 'Data' | 'Workflows' | 'Experience' | 'Security';
export const CAPABILITIES: readonly Capability[] = ['Intelligence', 'Automation', 'Integration', 'Data', 'Workflows', 'Experience', 'Security'];

export interface Product {
  id: ProductId;
  /** The identity this product is presented in (src/brand/brands.ts). */
  brand: BrandId;
  module: ModuleId;
  group: GroupId;
  tier: Tier;
  name: string;
  /** Used where the family is already clear from context, e.g. branches drawn off MINT. */
  shortName: string;
  /** Two or three words for diagrams. */
  essence: string;
  tagline: string;
  /** One spoken sentence, used when the product is named in overview narration. */
  pitch: string;
  capabilities: Capability[];
}

export interface ProductGroup {
  id: GroupId;
  name: string;
  tier: Tier;
  /** Brand accent, taken from the brand registry so the map never invents a colour. */
  color: string;
  /** The product that names the group on overview diagrams. */
  lead: ProductId;
}

export const groups: Record<GroupId, ProductGroup> = {
  mint: { id: 'mint', name: 'MINT', tier: 'flagship', color: BRANDS.mint.accent, lead: 'mint' },
  'research-space': { id: 'research-space', name: 'Research Space', tier: 'major', color: BRANDS['research-space'].accent, lead: 'research-space' },
  native: { id: 'native', name: 'Moderta Native', tier: 'major', color: BRANDS.native.accent, lead: 'native' },
  carbonmove: { id: 'carbonmove', name: 'CarbonMove', tier: 'independent', color: BRANDS.carbonmove.accent, lead: 'carbonmove' },
  climateviewpoint: { id: 'climateviewpoint', name: 'ClimateViewPoint', tier: 'independent', color: BRANDS.climateviewpoint.accent, lead: 'climateviewpoint' },
  etrust: { id: 'etrust', name: 'eTrust', tier: 'selected', color: BRANDS.etrust.accent, lead: 'etrust' },
};

/** Presentation order: flagship first, selected work last. */
export const GROUP_ORDER: readonly GroupId[] = ['mint', 'research-space', 'native', 'carbonmove', 'climateviewpoint', 'etrust'];

export const products: Record<ProductId, Product> = {
  mint: {
    id: 'mint',
    brand: 'mint',
    module: 'mint',
    group: 'mint',
    tier: 'flagship',
    name: 'MINT',
    shortName: 'MINT',
    essence: 'Intelligence foundation',
    tagline: 'From information to intelligence.',
    pitch: 'MINT is a general intelligence foundation that turns scattered information into connected, usable intelligence, and can be specialised for different domains.',
    capabilities: ['Intelligence', 'Data', 'Integration', 'Automation'],
  },
  'mint-it': {
    id: 'mint-it',
    brand: 'mint-it',
    module: 'mint-it',
    group: 'mint',
    tier: 'specialisation',
    name: 'MINT-IT',
    shortName: 'IT',
    essence: 'Sustainability intelligence',
    tagline: 'Sustainability intelligence for technology organisations.',
    pitch: 'MINT-IT applies MINT to the environmental footprint of technology organisations.',
    capabilities: ['Intelligence', 'Data', 'Workflows', 'Security'],
  },
  'mint-travel': {
    id: 'mint-travel',
    brand: 'mint-travel',
    module: 'mint-travel',
    group: 'mint',
    tier: 'specialisation',
    name: 'MINT Travel & Events',
    shortName: 'Travel & Events',
    essence: 'Travel & events context',
    tagline: 'Specialised intelligence for travel and event contexts.',
    pitch: 'MINT Travel & Events specialises the same foundation for the travel and events domain.',
    capabilities: ['Intelligence', 'Data', 'Experience'],
  },
  'mint-business': {
    id: 'mint-business',
    brand: 'mint-business',
    module: 'mint-business',
    group: 'mint',
    tier: 'specialisation',
    name: 'MINT Business',
    shortName: 'Business',
    essence: 'Business context',
    tagline: 'MINT intelligence specialised for business context.',
    pitch: 'MINT Business specialises it for business context.',
    capabilities: ['Intelligence', 'Data', 'Workflows'],
  },
  'research-space': {
    id: 'research-space',
    brand: 'research-space',
    module: 'research-space',
    group: 'research-space',
    tier: 'major',
    name: 'Moderta AI — Research Space',
    shortName: 'Research Space',
    essence: 'Human-approved AI work',
    tagline: 'AI proposes — people approve.',
    pitch: 'Moderta AI — Research Space is where AI tools propose drafts and people approve them.',
    capabilities: ['Automation', 'Integration', 'Workflows', 'Intelligence', 'Security'],
  },
  native: {
    id: 'native',
    brand: 'native',
    module: 'native',
    group: 'native',
    tier: 'major',
    name: 'Moderta Native',
    shortName: 'Native',
    essence: 'IT consulting & transformation',
    tagline: 'IT consulting, specialist services and AI-native transformation.',
    pitch: 'Moderta Native is the separate Moderta brand for IT consulting, specialist services and AI-native transformation.',
    capabilities: [],
  },
  carbonmove: {
    id: 'carbonmove',
    brand: 'carbonmove',
    module: 'carbonmove',
    group: 'carbonmove',
    tier: 'independent',
    name: 'CarbonMove',
    shortName: 'CarbonMove',
    essence: 'Verified climate action',
    tagline: 'Turning verified sustainable action into measurable climate impact.',
    pitch: 'CarbonMove turns everyday sustainable actions into verified, measurable climate impact.',
    capabilities: ['Data', 'Security', 'Experience', 'Intelligence'],
  },
  climateviewpoint: {
    id: 'climateviewpoint',
    brand: 'climateviewpoint',
    module: 'climateviewpoint',
    group: 'climateviewpoint',
    tier: 'independent',
    name: 'ClimateViewPoint',
    shortName: 'ClimateViewPoint',
    essence: 'Climate briefing',
    tagline: 'A focused sustainability briefing, built around relevance.',
    pitch: 'ClimateViewPoint brings sustainability news into one focused, personalised briefing.',
    capabilities: ['Data', 'Experience', 'Automation', 'Security'],
  },
  etrust: {
    id: 'etrust',
    brand: 'etrust',
    module: 'etrust',
    group: 'etrust',
    tier: 'selected',
    name: 'eTrust',
    shortName: 'eTrust',
    essence: 'Connected product journey',
    tagline: 'Digital ownership beyond the checkout.',
    pitch: 'eTrust is a platform Moderta designed and delivered to keep ownership, warranty and service connected to a product.',
    capabilities: ['Workflows', 'Data', 'Security', 'Experience', 'Integration'],
  },
};

export const PRODUCT_ORDER: readonly ProductId[] = ['mint', 'mint-it', 'mint-travel', 'mint-business', 'research-space', 'native', 'carbonmove', 'climateviewpoint', 'etrust'];

/** Joins names the way a person would say them: "A", "A and B", "A, B and C". */
export function spokenList(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
